import { useFormik } from 'formik'
import { FC, useEffect, useMemo } from 'react'
import {
  ButtonType,
  MaskType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../_metronic/helpers'
import { Button, FormInput, FormSelect, IOption, LoadingIndicator } from '../../../../../components'
import { enderecoSchema } from './validation/enderecoSchema'
import { useT } from '_metronic/i18n/Metronici18n'
import { CustomCard } from 'app/components/Card/CustomCard'
import {
  createEnderecoUsuarioAutenticado,
  getEnderecosUsuarioAutenticado,
  updateEnderecoUsuarioAutenticado,
} from 'app/modules/accounts/core/_requests'
import {
  EnderecoDtoCreate,
  EnderecoDtoUpdate,
} from 'app/modules/apps/empresa/empresas-list/core/_models'
import { getEstadoByUf } from 'app/modules/apps/atendimento_medicos/atendimentos-medico-list/core/_requests'

export const EnderecoDetails: FC = () => {
  const { t } = useT()

  const initialValues = {
    id: undefined as undefined | number,
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    uf: undefined as IOption<string> | undefined,
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => enderecoSchema(t), [t]),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (values.id) {
          const data: EnderecoDtoUpdate = {
            id: values.id,
            cep: values.cep,
            logradouro: values.logradouro,
            numero: values.numero,
            complemento: values.complemento ? values.complemento : null,
            bairro: values.bairro,
            cidade: values.cidade,
            uf: values.uf ? values.uf.value : '',
          }

          await updateEnderecoUsuarioAutenticado(data)
        } else {
          const data: EnderecoDtoCreate = {
            tipo: 1,
            cep: values.cep,
            logradouro: values.logradouro,
            numero: values.numero,
            complemento: values.complemento ? values.complemento : null,
            bairro: values.bairro,
            cidade: values.cidade,
            uf: values.uf ? values.uf.value : '',
          }
          await createEnderecoUsuarioAutenticado(data)
        }

        showAlert({
          icon: 'success',
          title: t('common.success'),
          text: 'Endereço atualizado com sucesso!',
        })
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const { setFieldValue } = formik

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getEnderecosUsuarioAutenticado()
        const endereco = data[0]
        const ufEnd = endereco?.uf ? await getEstadoByUf(endereco?.uf as string) : undefined

        setFieldValue('id', endereco?.id)
        setFieldValue('cep', endereco?.cep || '')
        setFieldValue('logradouro', endereco?.logradouro || '')
        setFieldValue('numero', endereco?.numero || '')
        setFieldValue('complemento', endereco?.complemento || '')
        setFieldValue('bairro', endereco?.bairro || '')
        setFieldValue('cidade', endereco?.cidade || '')
        setFieldValue(
          'uf',
          endereco?.uf ? { label: ufEnd?.descricao, value: ufEnd?.sigla } : undefined
        )
      } catch (error) {
        showError(error)
      }
    }

    load()
  }, [setFieldValue])

  return (
    <CustomCard title={t('endereco.label')}>
      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='row mb-3'>
          <div className='col-md-8'>
            <FormInput formik={formik} name='logradouro' entity='logradouro' isRequired />
          </div>
          <div className='col-md-4'>
            <FormInput formik={formik} name='numero' entity='numero' isRequired />
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col-md-8'>
            <FormInput formik={formik} name='complemento' entity='complemento' />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='cep'
              label='CEP'
              mask={MaskType.CEP}
              placeholder='00.000-000'
              isRequired
            />
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col-md-8'>
            <FormInput formik={formik} name='bairro' entity='bairro' isRequired />
          </div>
          <div className='col-md-4'>
            <FormInput formik={formik} name='cidade' entity='cidade' isRequired />
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col-md-8'>
            <FormSelect
              formik={formik}
              fetchOptions={OptionsHelper.getOptionsEstadosUf}
              name='uf'
              entity='estado'
              isDisabled={formik.isSubmitting}
              isRequired
            />
          </div>
        </div>
        <div className='d-flex flex-end'>
          <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
            {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
          </Button>
        </div>
      </form>
    </CustomCard>
  )
}
